/**
 * Stage visualization component
 * Shows 3-stage progress: Principal → Regulatory → Master Data
 */
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import type {
  ApprovalCurrentStage,
  StageValidation,
} from "../../../../types/types.Workflow";
import { Avatar } from "../../../../components/Avatar/Avatar";
import { FlagIcon, FlagXIcon } from "../../../../components/Icons/Icons";
import {
  TextMdSemibold,
  TextSmRegular,
} from "../../../../components/Typography/TypographyV2";
import {
  PrimaryButtonLargeV2,
  DestructiveButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import { StatusBadge } from "../../../../components/Status/Status";
import { useAuthContext } from "../../../../components/Auth";
import ReactTooltip from "react-tooltip";
import { ApprovalAttributes } from "./hooks/useApprovalDetail";
import { useCallback } from "react";
import { buildStageValidationTip } from "./stageValidationTip";
import {
  approvalStatusColor,
  approvalStatusLabel,
  getApproverFlagKind,
  stageMatchesIdentifier,
} from "./utils";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const StageCard = styled.div<{ isActive: boolean; isInProgress: boolean }>`
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme, isActive, isInProgress }) => {
      if (isInProgress) {
        return theme.resolve("color_border_caution");
      }
      if (isActive) {
        return theme.resolve("color_border_brand");
      }
      return theme.resolve("color_border_lighter");
    }};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const StageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

const ApproversList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const ApproverRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const ApproverInfo = styled.div`
  flex: 1;
  margin-top: -2px; /* Subtle adjustment to align text with avatar */
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  margin-top: ${({ theme }) => theme.resolve("spacing_md")};
  padding-top: ${({ theme }) => theme.resolve("spacing_md")};
  border-top: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

interface ApprovalStageVisualizationProps {
  stages: ApprovalCurrentStage[];
  currentStage: string;
  approvalId?: string;
  canTakeAction?: boolean;
  stageValidation?: StageValidation;
  attributes?: ApprovalAttributes;
  onApprove?: () => void;
  onReject?: () => void;
}

export function ApprovalStageVisualization({
  stages,
  currentStage,
  onApprove,
  onReject,
  stageValidation,
  attributes,
}: ApprovalStageVisualizationProps) {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const theme = useTheme();

  const getStageValidationTip = useCallback((): string | undefined => {
    if (!stageValidation) {
      return undefined;
    }
    return buildStageValidationTip(t, stageValidation, attributes);
  }, [attributes, stageValidation, t]);

  return (
    <Container>
      {stages.map((stage) => {
        const isActive = stageMatchesIdentifier(stage, currentStage);
        const isInProgress = stage.stage_status === "in_progress";
        const is_user_approver = stage.approvers.some(
          (approver) =>
            isActive && approver.approver_id === user?.id && isInProgress
        );
        const showButtons = is_user_approver && onApprove && onReject;

        return (
          <StageCard
            key={stage.slug || stage.name}
            isActive={isActive}
            isInProgress={isInProgress}
          >
            <StageHeader>
              <TextMdSemibold>{stage.display_name}</TextMdSemibold>
              <StatusBadge
                color={approvalStatusColor(stage.stage_status)}
                text={approvalStatusLabel(stage.stage_status, t)}
              />
            </StageHeader>

            <ApproversList>
              {stage.approvers.map((approver) => {
                const flagKind = getApproverFlagKind({
                  decision: approver.approver_decision,
                  isStageInProgress: isInProgress,
                  approverId: approver.approver_id,
                  assigneeId: stage.assignee?.id,
                });

                return (
                  <ApproverRow key={approver.approver_id}>
                    <Avatar name={approver.approver_name} size="medium" />
                    <ApproverInfo>
                      <TextSmRegular>{approver.approver_name}</TextSmRegular>
                    </ApproverInfo>
                    {flagKind === "approved" && (
                      <FlagIcon fill={theme.resolve("color_icon_success")} />
                    )}
                    {flagKind === "rejected" && (
                      <FlagXIcon fill={theme.resolve("color_icon_error")} />
                    )}
                    {flagKind === "assignee" && (
                      <FlagIcon fill={theme.resolve("color_icon_disabled")} />
                    )}
                  </ApproverRow>
                );
              })}
            </ApproversList>

            {showButtons && (
              <ActionButtons>
                <DestructiveButtonLargeV2 onClick={onReject}>
                  {t("Reject")}
                </DestructiveButtonLargeV2>
                <PrimaryButtonLargeV2
                  onClick={onApprove}
                  disabled={!stageValidation?.eligible}
                  datatip={getStageValidationTip() ?? ""}
                  datafor="approval-stage-validation-tip"
                >
                  {t("Approve")}
                </PrimaryButtonLargeV2>
                <ReactTooltip
                  delayHide={500}
                  id="approval-stage-validation-tip"
                  effect="solid"
                />
              </ActionButtons>
            )}
          </StageCard>
        );
      })}
    </Container>
  );
}
